import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SectionHeader from "@/components/SectionHeader";
import PricingPlans from "@/components/PricingPlans";
import AccuracyProof from "@/components/AccuracyProof";
import FAQAccordion from "@/components/FAQAccordion";
import RiskDisclosure from "@/components/RiskDisclosure";
import MarketCanvas from "@/components/MarketCanvas";
import { Reveal, MaskReveal } from "@/components/Reveal";
import { COMMUNITY_FEATURES, ENTRADE_DASHBOARD, FAQS } from "@/data/content";

const steps = [
  { n: "01", title: "Choose your plan", text: "3 months, 1 year or lifetime — pick the access that fits." },
  { n: "02", title: "Join the community", text: "Get immediate entry to the T Plus One Premium Community, entirely online." },
  { n: "03", title: "Stay close to the market", text: "Receive Indian and Forex insights, educational trade setups and market notes." },
];

const EnTrade = () => {
  useEffect(() => {
    document.title = "EnTrade Membership — Premium Trading Community | T Plus One";
  }, []);

  const faqs = FAQS.filter((f) => f.category === "EnTrade" || f.category === "Risk");

  return (
    <main data-testid="entrade-page">
      {/* HERO — dark product reveal */}
      <section className="relative bg-coal text-ivory grain overflow-hidden pt-40 pb-24 lg:pt-52 lg:pb-32">
        <div className="absolute inset-0 opacity-50" aria-hidden="true"><MarketCanvas dark /></div>
        <div className="relative container-x">
          <Reveal><p className="eyebrow-dark">T Plus One Membership</p></Reveal>
          <MaskReveal as="h1" lines={["EnTrade.", "Stay closer to the market."]} className="mt-6 text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.05]" />
          <Reveal delay={0.3}>
            <p className="mt-8 max-w-xl text-base sm:text-lg text-[#A0A09C] leading-relaxed">
              A completely online membership for traders who want ongoing access to the T Plus One Premium Community, market insights and trade recommendations — without enrolling in a full course.
            </p>
          </Reveal>
          <Reveal delay={0.4}>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <a href="#plans" data-testid="entrade-hero-cta" className="group inline-flex items-center justify-center gap-2 rounded-full bg-ivory text-ink px-8 py-4 text-sm font-medium transition-colors hover:bg-pine hover:text-ivory">
                View Membership Plans <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5" />
              </a>
              <Link to="/contact?interest=EnTrade%20—%203%20Months" data-testid="entrade-hero-enquire" className="link-arrow inline-flex items-center justify-center gap-2 rounded-full border border-ivory/25 px-8 py-4 text-sm font-medium transition-colors hover:border-pine-2">
                Enquire <ArrowRight size={15} className="arrow" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHAT ENTRADE IS */}
      <section className="py-24 lg:py-32 border-t border-line">
        <div className="container-x grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <SectionHeader eyebrow="The Product" lines={["One membership.", "The entire community."]} sub="EnTrade is digital-only: insights, recommendations, discussions and notes — delivered wherever you are." />
          </div>
          <Reveal delay={0.1}>
            <div className="rounded-sm border border-line bg-white shadow-[0_32px_80px_-32px_rgba(16,16,16,0.18)] overflow-hidden">
              <div className="flex items-center gap-2 border-b border-line px-5 py-3.5">
                <span className="w-2 h-2 rounded-full bg-line" /><span className="w-2 h-2 rounded-full bg-line" /><span className="w-2 h-2 rounded-full bg-pine/60" />
                <span className="ml-3 font-mono text-[10px] uppercase tracking-[0.18em] text-tertiary">EnTrade · Member View</span>
              </div>
              <div className="grid sm:grid-cols-2">
                {ENTRADE_DASHBOARD.map((d, i) => (
                  <div key={d.label} className={`p-6 ${i % 2 === 0 ? "sm:border-r" : ""} ${i < 4 ? "border-b" : ""} border-line`}>
                    <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-tertiary">{d.label}</p>
                    <p className="mt-2 text-sm font-medium">{d.value}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* COMMUNITY FEATURES */}
      <section className="py-24 lg:py-32 bg-white border-t border-line">
        <div className="container-x">
          <SectionHeader eyebrow="Inside EnTrade" lines={["Indian markets. Forex.", "One community."]} sub="Coverage across both markets, with educational trade recommendations shared with context — never as profit promises." />
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {COMMUNITY_FEATURES.map((f, i) => (
              <Reveal key={f.title} delay={i * 0.06} className="h-full">
                <div className="h-full rounded-sm border border-line bg-ivory p-7" data-testid={`entrade-feature-${i}`}>
                  <p className="font-mono text-[10px] text-pine tracking-[0.2em]">{String(i + 1).padStart(2, "0")}</p>
                  <h3 className="mt-3 text-base font-medium tracking-tight">{f.title}</h3>
                  <p className="mt-2 text-sm text-dim leading-relaxed">{f.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2} className="mt-10 max-w-md">
            <AccuracyProof dark={false} />
          </Reveal>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24 lg:py-32 border-t border-line">
        <div className="container-x">
          <SectionHeader eyebrow="How It Works" lines={["Three steps to access."]} />
          <div className="mt-14 grid md:grid-cols-3 gap-5">
            {steps.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.1}>
                <div className="border-t-2 border-ink pt-6" data-testid={`entrade-step-${s.n}`}>
                  <p className="font-mono text-sm text-pine">{s.n}</p>
                  <h3 className="mt-3 text-xl tracking-tight">{s.title}</h3>
                  <p className="mt-2 text-sm text-dim leading-relaxed">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PLANS */}
      <section id="plans" className="py-24 lg:py-32 bg-white border-t border-line scroll-mt-20">
        <div className="container-x">
          <SectionHeader eyebrow="Membership Plans" lines={["Choose your access."]} sub="No hype, no urgency tactics. Pick a plan and start receiving community access online." />
          <div className="mt-14">
            <PricingPlans />
          </div>
          <Reveal delay={0.2}>
            <p className="mt-8 text-xs text-tertiary text-center">Membership is activated after enrolment confirmation. See our <Link to="/legal/refund" className="underline underline-offset-2 hover:text-ink">refund policy</Link> for details.</p>
          </Reveal>
        </div>
      </section>

      {/* FAQ + RISK */}
      <section className="py-24 lg:py-32 border-t border-line">
        <div className="container-x grid lg:grid-cols-[1fr_1.4fr] gap-12">
          <div>
            <SectionHeader eyebrow="FAQ" lines={["EnTrade questions."]} />
            <Reveal delay={0.2} className="mt-10">
              <RiskDisclosure />
            </Reveal>
          </div>
          <FAQAccordion items={faqs} />
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative bg-coal text-ivory grain py-28 lg:py-40 text-center overflow-hidden">
        <div className="relative container-x max-w-2xl mx-auto">
          <Reveal><p className="eyebrow-dark">EnTrade</p></Reveal>
          <MaskReveal as="h2" lines={["The market won’t wait.", "Neither should your access."]} className="mt-6 text-4xl sm:text-5xl tracking-tight leading-[1.1]" />
          <Reveal delay={0.25}>
            <a href="#plans" data-testid="entrade-final-cta" className="mt-10 inline-flex items-center gap-2 rounded-full bg-ivory text-ink px-8 py-4 text-sm font-medium transition-colors hover:bg-pine hover:text-ivory">
              Choose a Plan <ArrowRight size={15} />
            </a>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default EnTrade;
